import { useEffect, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { getMaxSlippageFactor } from "#/config/slippage";
import { type Option } from "#/types/options";
import { formatPercent } from "#/utils/valueFormatters";

export const useValidateOptionLimitPrice = ({
  option,
  limitPrice,
}: {
  option?: Option;
  limitPrice?: string;
}) => {
  const [limitPriceError, setLimitPriceError] = useState("");

  const minBuyPrice = option?.asks?.at(-1)?.price ?? 0;
  const maxSellPrice = option?.bids?.at(0)?.price ?? 0;

  const validatePrice = useDebounceCallback(() => {
    if (!option || !limitPrice) {
      setLimitPriceError("");
      return;
    }

    const { isBuy, delta, markPrice } = option;

    const limitPriceNum = Number(limitPrice);
    const isPriceOutOfBounds = isBuy
      ? minBuyPrice && limitPriceNum < minBuyPrice
      : maxSellPrice && limitPriceNum > maxSellPrice;
    const prefix = isBuy ? "Max" : "Min";

    if (isPriceOutOfBounds) {
      setLimitPriceError(
        `${prefix} price is ${isBuy ? "below" : "above"} best orderbook price.`,
      );
      return;
    }

    if (!markPrice) {
      setLimitPriceError("");
      return;
    }

    const slippage = getMaxSlippageFactor(true, delta ?? 0);
    const priceWithTolerance = markPrice * (1 + slippage * (isBuy ? 1 : -1));

    const isPriceOutOfTolerance = isBuy
      ? limitPriceNum > priceWithTolerance
      : limitPriceNum < priceWithTolerance;
    const formattedSlippage = formatPercent(slippage, {
      signDisplay: "never",
    });
    const error = isPriceOutOfTolerance
      ? `Max slippage for this option delta is ${formattedSlippage}. Try limit order.`
      : "";

    setLimitPriceError(error);
  }, 800);

  useEffect(() => {
    validatePrice();
  }, [
    limitPrice,
    option?.isBuy,
    option?.delta,
    option?.markPrice,
    option?.instrument_name,
    minBuyPrice,
    maxSellPrice,
    validatePrice,
  ]);

  return { limitPriceError };
};
